// [#12] UX review feedback prompt — a compact "how was that?" card shown after a
// user finishes a flow (placing an order, posting a listing, etc.). One rating +
// an optional comment; dismisses on submit or skip. Stored per-context so a user
// isn't nagged again for the same kind of flow in this browser.
import { useId, useState } from "react";
import { Loader2, Star, CheckCircle2, X } from "lucide-react";
import { api } from "../lib/api";

type Context = "order" | "contract" | "rfq" | "post" | "general";

const seenKey = (c: Context) => `uxfb_seen_${c}`;
const RATING_LABELS = ["Very poor", "Poor", "Okay", "Good", "Excellent"];

function alreadySeen(c: Context): boolean {
  try { return localStorage.getItem(seenKey(c)) === "1"; } catch { return false; }
}
function markSeen(c: Context) {
  try { localStorage.setItem(seenKey(c), "1"); } catch { /* ignore */ }
}

export function UxFeedbackPrompt({
  context, refId, title = "How was that experience?", once = true,
}: {
  context: Context;
  refId?: string;
  title?: string;
  once?: boolean;
}) {
  const [hidden, setHidden] = useState(() => once && alreadySeen(context));
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleId = useId();

  if (hidden) return null;

  function dismiss() {
    if (once) markSeen(context);
    setHidden(true);
  }

  async function submit() {
    if (!rating || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.submitFeedback({ context, refId, rating, comment: comment.trim() || undefined });
      if (once) markSeen(context);
      setDone(true);
    } catch {
      // [content.ux-feedback](../../../docs/product/public-content.md#content-ux-feedback): retain the draft for retry; only a successful write earns thanks/seen.
      setError("Couldn't send feedback. Please try again.");
    }
    finally { setBusy(false); }
  }

  if (done) {
    return (
      <div role="status" className="rounded-2xl border border-line bg-navy-50/50 px-5 py-6 text-center">
        <CheckCircle2 aria-hidden="true" className="mx-auto mb-2 h-7 w-7 text-brand-green" />
        <p className="text-sm font-semibold text-navy">Thanks for your feedback</p>
        <p className="mt-1 text-xs text-mut">It helps us improve KontrataPH.</p>
      </div>
    );
  }

  const shown = hover || rating;
  return (
    <section aria-labelledby={titleId} className="relative rounded-2xl border border-line bg-navy-50/50 px-4 pb-5 pt-6 text-center sm:px-6">
      <button type="button" disabled={busy} onClick={dismiss} aria-label="Dismiss feedback" className="absolute right-1 top-1 grid h-9 w-9 place-items-center rounded-full text-mut hover:bg-white hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 disabled:opacity-40"><X aria-hidden="true" className="h-4 w-4" /></button>
      <h4 id={titleId} className="px-3 text-base font-semibold text-navy">{title}</h4>
      <div className="mt-4 flex justify-center gap-2" role="radiogroup" aria-label="Rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            aria-label={`${n} star${n === 1 ? "" : "s"}: ${RATING_LABELS[n - 1]}`}
            aria-checked={rating === n}
            tabIndex={rating === n || (!rating && n === 1) ? 0 : -1}
            disabled={busy}
            role="radio"
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => { setRating(n); setError(null); }}
            onKeyDown={(event) => {
              const next = event.key === "Home" ? 1 : event.key === "End" ? 5 : event.key === "ArrowRight" || event.key === "ArrowDown" ? n % 5 + 1 : event.key === "ArrowLeft" || event.key === "ArrowUp" ? (n + 3) % 5 + 1 : null;
              if (next === null) return;
              event.preventDefault();
              setRating(next);
              setHover(0);
              setError(null);
              event.currentTarget.parentElement?.querySelector<HTMLButtonElement>(`[data-rating="${next}"]`)?.focus();
            }}
            data-rating={n}
            className={`grid h-11 w-11 min-w-0 place-items-center rounded-xl border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 focus-visible:ring-offset-2 disabled:opacity-60 ${n <= shown ? "border-brand-gold/50 bg-brand-gold/10" : "border-line bg-white hover:border-navy/30"}`}
          >
            <Star aria-hidden="true" className={`h-6 w-6 ${n <= shown ? "fill-brand-gold text-brand-gold" : "text-navy-200"}`} />
          </button>
        ))}
      </div>
      <p aria-live="polite" className="mt-2 min-h-5 text-xs text-mut">{shown ? RATING_LABELS[shown - 1] : ""}</p>
      {rating > 0 && (
        <div className="mt-3 space-y-3 text-left">
          <textarea
            aria-label="Optional feedback"
            disabled={busy}
            className="field min-h-20 resize-y py-2.5 text-sm"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Anything we could make easier? (optional)"
            maxLength={2000}
          />
          {error && <p role="alert" className="text-xs text-brand-red">{error}</p>}
          <div className="flex items-center justify-end gap-3">
            <button type="button" disabled={busy} onClick={dismiss} className="px-2 py-2 text-sm font-medium text-mut hover:text-navy disabled:opacity-60">Skip</button>
            <button type="button" onClick={submit} disabled={busy} className="btn-red h-11 px-4 text-sm disabled:opacity-60">
              {busy ? <><Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />Sending…</> : "Send feedback"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
